import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Upload, Loader2, CheckCircle, XCircle, ArrowRight, Database, RotateCcw, Square, Archive, AlertTriangle } from "lucide-react";
import { formatJamaicaDateTime as formatDateTime } from "@/lib/datetime";
import { Progress } from "@/components/ui/progress";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ExportProgress {
  id: string;
  status: string;
  mode: string;
  totalQuestions: number;
  totalBatches: number;
  batchesCompleted: number;
  questionsSent: number;
  questionsIdMapped: number;
  lastQuestionId: string | null;
  errors: string[];
  startedAt: string;
  completedAt: string | null;
}

export default function QuizzativeBulkExport() {
  const { toast } = useToast();
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const { data: statusData } = useQuery<{ status: ExportProgress | null }>({
    queryKey: ["/api/admin/quizzative/bulk-export/status"],
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && status.status === "running") return 2000;
      return 10000;
    },
  });

  const progress = statusData?.status;
  const isRunning = progress?.status === "running";
  const isFailed = progress?.status === "failed";
  const canResume = isFailed && progress?.lastQuestionId;

  const startExport = useMutation({
    mutationFn: async (params: { onlyNew: boolean; resume?: boolean }) => {
      const res = await apiRequest("POST", "/api/admin/quizzative/bulk-export", params);
      return res.json();
    },
    onSuccess: (_, variables) => {
      const action = variables.resume ? "resumed" : "started";
      toast({ title: `Bulk export ${action}`, description: "Questions are being sent to Quizzative in batches." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative/bulk-export/status"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to start export", description: err.message, variant: "destructive" });
    },
  });

  const archiveAll = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/questions/archive-all");
      return res.json();
    },
    onSuccess: (data: { message: string; archived: number }) => {
      toast({ title: "Questions archived", description: data.message });
      setShowArchiveConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative/bulk-export/status"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to archive", description: err.message, variant: "destructive" });
      setShowArchiveConfirm(false);
    },
  });

  const cancelExport = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/quizzative/bulk-export/cancel");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Stop requested", description: "The export will stop after the current batch finishes." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative/bulk-export/status"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to stop export", description: err.message, variant: "destructive" });
    },
  });

  const percentComplete = progress && progress.totalBatches > 0
    ? Math.round((progress.batchesCompleted / progress.totalBatches) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Bulk Export to Quizzative</h2>
        <p className="text-muted-foreground">
          Send all EduFocal questions to Quizzative so they can assign IDs and manage them going forward
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Start Export
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This sends questions to Quizzative in batches of 100. Each question gets a Quizzative ID back, 
            which is stored on your end for future result tracking. If a batch fails after 3 retries, 
            the export stops and you can resume from where it left off.
          </p>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => startExport.mutate({ onlyNew: false })}
              disabled={isRunning || startExport.isPending}
            >
              {startExport.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
              Export All Questions
            </Button>
            <Button
              variant="outline"
              onClick={() => startExport.mutate({ onlyNew: true })}
              disabled={isRunning || startExport.isPending}
            >
              {startExport.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              Export Only New (No Quizzative ID)
            </Button>
            {isRunning && (
              <Button
                variant="destructive"
                onClick={() => cancelExport.mutate()}
                disabled={cancelExport.isPending}
              >
                {cancelExport.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Square className="h-4 w-4 mr-2" />}
                Stop Export
              </Button>
            )}
            {canResume && (
              <Button
                variant="outline"
                onClick={() => startExport.mutate({ onlyNew: progress.mode === "new_only", resume: true })}
                disabled={isRunning || startExport.isPending}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Resume Failed Export
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {progress && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
              <span className="flex items-center gap-2">
                {isRunning && <Loader2 className="h-5 w-5 animate-spin" />}
                {progress.status === "completed" && <CheckCircle className="h-5 w-5 text-green-500" />}
                {isFailed && <XCircle className="h-5 w-5 text-red-500" />}
                Export Progress
              </span>
              <Badge variant={isRunning ? "default" : progress.status === "completed" ? "secondary" : "destructive"}>
                {progress.status}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isRunning && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span>Batch {progress.batchesCompleted + 1} of {progress.totalBatches}</span>
                  <span>{percentComplete}%</span>
                </div>
                <Progress value={percentComplete} className="h-2" />
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Total Questions</p>
                <p className="text-xl font-bold">{progress.totalQuestions}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sent</p>
                <p className="text-xl font-bold">{progress.questionsSent}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">IDs Mapped</p>
                <p className="text-xl font-bold text-green-600 dark:text-green-400">{progress.questionsIdMapped}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Errors</p>
                <p className="text-xl font-bold text-red-600 dark:text-red-400">{progress.errors.length}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              {progress.startedAt && <span>Started: {formatDateTime(progress.startedAt)}</span>}
              {progress.completedAt && <span>Finished: {formatDateTime(progress.completedAt)}</span>}
              <span>Mode: {progress.mode === "new_only" ? "New only" : "All questions"}</span>
            </div>

            {isFailed && canResume && (
              <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                <p className="text-sm">
                  Export stopped due to a batch failure. You can resume from where it left off using the "Resume Failed Export" button above.
                </p>
              </div>
            )}

            {progress.errors.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-red-600 dark:text-red-400">Errors:</p>
                <div className="max-h-40 overflow-y-auto bg-muted/50 rounded-md p-3 space-y-1">
                  {progress.errors.map((err, i) => (
                    <p key={i} className="text-xs text-red-600 dark:text-red-400 font-mono">{err}</p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>How it works</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
            <li>Questions are sent to Quizzative in batches of up to 100</li>
            <li>Quizzative assigns each question a unique Quizzative ID</li>
            <li>EduFocal stores the Quizzative ID on each question for future tracking</li>
            <li>Failed batches are retried up to 3 times before stopping</li>
            <li>If it stops, you can resume from where it left off</li>
            <li>Once complete, all student results flow to Quizzative with proper IDs</li>
          </ol>
        </CardContent>
      </Card>

      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5" />
            Archive All Questions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Archive all current questions on the platform. Archived questions are hidden from students and active
            question pools, but student history and past progress are fully preserved. Use this before syncing
            fresh questions from Quizzative.
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant="destructive"
              onClick={() => setShowArchiveConfirm(true)}
              disabled={archiveAll.isPending}
            >
              {archiveAll.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Archive className="h-4 w-4 mr-2" />}
              Archive All Questions
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Archive All Questions?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will archive every question currently on the platform. Students will no longer see these
              questions, but all their past progress and history is preserved. You should sync fresh questions
              from Quizzative after archiving.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => archiveAll.mutate()}
              className="bg-destructive text-destructive-foreground"
            >
              {archiveAll.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Yes, Archive All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
